import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { NotificationRuleDialog } from "@/components/notifications/NotificationRuleDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  deleteNotification,
  fetchNotifications,
  type NotificationRule,
} from "@/lib/traccar-api";

const getNotificatorLabel = (type: string, t: (key: string) => string) =>
  type === "firebase" ? t("notifications.config.mobileApp") : type;

const Notifications = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | undefined>(undefined);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);

  const { data: notificationRules = [], isLoading: rulesLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast.success(t("notifications.config.deleted"));
      setDeletingRuleId(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setDeletingRuleId(null);
    },
  });

  const getTypeLabelForRule = (type: string) => {
    const key = `notifications.events.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  const openCreateDialog = () => {
    setEditingRule(undefined);
    setRuleDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border-b">
          <div>
            <h1 className="text-2xl font-bold">{t("notifications.title")}</h1>
            <p className="text-muted-foreground">{t("notifications.config.subtitle")}</p>
          </div>
          <Button className="gap-2" onClick={openCreateDialog}>
            <Plus className="h-4 w-4" />
            {t("notifications.config.addRule")}
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            <div>
              <h2 className="text-lg font-semibold">{t("notifications.config.title")}</h2>
            </div>

            {rulesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : notificationRules.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Bell className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">{t("notifications.config.noRules")}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("notifications.config.noRulesHint")}
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("notifications.config.type")}</TableHead>
                      <TableHead>{t("notifications.config.channels")}</TableHead>
                      <TableHead className="w-[100px]">{t("devices.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notificationRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">
                          <div>{getTypeLabelForRule(rule.type)}</div>
                          {rule.type === "deviceIdle" && rule.attributes?.minIdlePeriod && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {Math.round((rule.attributes.minIdlePeriod as number) / 60)}{" "}
                              {t("notifications.config.idleTimeUnit")}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {rule.notificators ? (
                              rule.notificators.split(",").filter(Boolean).map((notificator) => (
                                <Badge key={notificator} variant="secondary" className="capitalize text-xs">
                                  {getNotificatorLabel(notificator, t)}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingRule(rule);
                                setRuleDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setDeletingRuleId(rule.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <NotificationRuleDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        rule={editingRule}
      />

      <AlertDialog open={deletingRuleId !== null} onOpenChange={(open) => !open && setDeletingRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notifications.config.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("notifications.config.deleteConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingRuleId !== null && deleteMutation.mutate(deletingRuleId)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Notifications;
